import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Slider,
  Divider,
  useTheme,
  IconButton,
} from "@mui/material";
import { SlidersHorizontal, ChevronDown, ChevronUp, X } from "lucide-react";
import { tokens } from "../../../theme";
import { getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { MarketProduct } from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { PRODUCT_CATEGORY_CODETABLE, USER_ROLES } from "../../../constants/applicationConstants";

export interface FilterState {
  favoritesOnly: boolean;
  selectedCategories: string[];
  selectedCompositions: string[];
  selectedManufacturers: string[];
  allowedStatuses: string[];
  selectedPurchaseAndBill: string[]; // CS-1765
}

interface Props {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  products: MarketProduct[];
  hideStyle?: boolean;
  onClearAll?: () => void;
  activeFiltersCount?: number;
  onClose?: () => void; // Prop for closing drawer on mobile
}


const MarketFilterSidebar: React.FC<Props> = ({
  filters,
  onFiltersChange,
  products,
  hideStyle = false,
  onClearAll,
  activeFiltersCount = 0,
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { userInfo } = useAuthSlice();
  const canAccessPurchaseAndBillFilter =
    userInfo?.userRole === USER_ROLES.ROLE_SUPERADMIN ||
    userInfo?.userRole === USER_ROLES.ROLE_ADMIN; // CS-1765

  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();

  const statusOptions = useMemo(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const isAllowedRoles = (userInfo?.userRole === USER_ROLES.ROLE_SUPERADMIN || userInfo?.userRole === USER_ROLES.ROLE_ADMIN);
      const hasTbdProducts = products.some(
        (p) => (p.productStatus || "").toUpperCase() === "TBD"
      );
      if(isAllowedRoles) {
        return getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductStatus").map((item: any) => ({
          id: item.keyName,
          label: item.displayValue,
        }));
      }else{
        return getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductStatus").filter((item: any) => {
          if (item.keyName === "INPROGRESS" || item.keyName === "INACTIVE") return false;
          // Hide TBD (Coming Soon) if no products have TBD status, including when no products are loaded
          if (item.keyName === "TBD" && !hasTbdProducts) return false;
          return true;
        }).map((item: any) => ({
          id: item.keyName,
          label: item.displayValue,
        }));
      }
    }
    return [];
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus, products, userInfo]);
  const careCategoryOptions = useMemo(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      return getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE).map((item: any) => ({
        id: item.keyName,
        label: item.displayValue,
      }));
    }
    return [];
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const visibleCareCategoryOptions = useMemo(() => {
    const availableCategories = new Set(
      products
        .filter((product) => (product.productStatus || "").toUpperCase().trim() !== "INACTIVE")
        .map((product) => String(product.productCategory || "").trim())
        .filter((category) => !!category)
    );

    return careCategoryOptions.filter((opt) =>
      availableCategories.has(String(opt.id || "").trim())
    );
  }, [careCategoryOptions, products]);

  const borderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(0,0,0,0.12)";
  const textColor = isDark ? colors.grey[100] : colors.grey[100];
  const subTextColor = isDark ? colors.grey[400] : colors.grey[400];

  const checkboxSx = {
    color: isDark ? "rgba(148,163,184,0.5)" : "rgba(0,0,0,0.4)",
    "&.Mui-checked": {
      color: "#14b8a6",
    },
  };

  const rowSx = {
    display: "flex",
    alignItems: "center",
    borderRadius: "8px",
    mx: -0.5,
    px: 0.5,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: isDark ? "rgba(20,184,166,0.07)" : "rgba(20,184,166,0.05)",
    },
  };

  const sectionTitleSx = {
    fontWeight: 800,
    fontSize: "0.78rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    color: textColor,
    mb: 0.75,
  };

  const sliderSx = {
    color: "#14b8a6",
    "& .MuiSlider-thumb": {
      backgroundColor: "#14b8a6",
      "&:hover, &.Mui-focusVisible": {
        boxShadow: "0 0 0 8px rgba(20,184,166,0.16)",
      },
    },
    "& .MuiSlider-track": {
      backgroundColor: "#14b8a6",
      border: "none",
    },
    "& .MuiSlider-rail": {
      backgroundColor: isDark ? "rgba(148,163,184,0.3)" : "rgba(0,0,0,0.2)",
    },
  };

  const dynamicCategories = useMemo(() => {
    const groups: Record<string, Set<string>> = {};

    // Initialize groups from code table options using id (keyname)
    visibleCareCategoryOptions.forEach(opt => {
      groups[opt.id] = new Set();
    });

    products.forEach((p) => {
      const status = (p.productStatus || "").toUpperCase().trim();
      if (status === "INACTIVE") return;

      // Match product's raw category against prdProductCategory keynames
      const rawCat = (p.productCategory || "").trim();
      const matchingKey = Object.keys(groups).find(k => k === rawCat);
      if (matchingKey && p.composition)
        groups[matchingKey].add(p.composition);
    });

    const finalGroups: Record<string, string[]> = {};
    Object.keys(groups).forEach(key => {
      // Sort compositions alphabetically
      finalGroups[key] = Array.from(groups[key]).sort((a, b) => a.localeCompare(b));
    });

    return finalGroups;
  }, [products, visibleCareCategoryOptions]);

  const manufacturerOptions = useMemo(() => {
    return Array.from(
      new Set(
        products
          .map((p) => (p.manufacturerName || "").trim())
          .filter((name) => !!name)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const toggleFavoritesOnly = () =>
    onFiltersChange({
      ...filters,
      favoritesOnly: !filters.favoritesOnly,
    });

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    const exists =
      filters.selectedCategories.includes(category);

    onFiltersChange({
      ...filters,
      selectedCategories: exists
        ? filters.selectedCategories.filter(
          (c) => c !== category
        )
        : [...filters.selectedCategories, category],
    });
  };

  const toggleCategoryExpansion = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const toggleComposition = (composition: string) => {
    const exists =
      filters.selectedCompositions.includes(composition);

    onFiltersChange({
      ...filters,
      selectedCompositions: exists
        ? filters.selectedCompositions.filter(
          (c) => c !== composition
        )
        : [...filters.selectedCompositions, composition],
    });
  };

  const toggleStatus = (status: string) => {
    const exists =
      filters.allowedStatuses.includes(status);

    onFiltersChange({
      ...filters,
      allowedStatuses: exists
        ? filters.allowedStatuses.filter(
          (s) => s !== status
        )
        : [...filters.allowedStatuses, status],
    });
  };

  const toggleManufacturer = (manufacturer: string) => {
    const exists = filters.selectedManufacturers.includes(manufacturer);

    onFiltersChange({
      ...filters,
      selectedManufacturers: exists
        ? filters.selectedManufacturers.filter((m) => m !== manufacturer)
        : [...filters.selectedManufacturers, manufacturer],
    });
  };

  // CS-1765 - Toggle Purchase & Bill values for admin and super admin users
  const togglePurchaseAndBill = (value: string) => {
    const exists = filters.selectedPurchaseAndBill.includes(value);

    onFiltersChange({
      ...filters,
      selectedPurchaseAndBill: exists
        ? filters.selectedPurchaseAndBill.filter((item) => item !== value)
        : [...filters.selectedPurchaseAndBill, value],
    });
  };

  useEffect(() => {
    // Avoid pruning preselected statuses before products are loaded.
    if (products.length === 0) return;

    const visibleStatusIds = new Set(statusOptions.map((s) => s.id));
    const nextAllowedStatuses = filters.allowedStatuses.filter((s) =>
      visibleStatusIds.has(s)
    );

    if (nextAllowedStatuses.length !== filters.allowedStatuses.length) {
      onFiltersChange({
        ...filters,
        allowedStatuses: nextAllowedStatuses,
      });
    }
  }, [statusOptions, filters, onFiltersChange, products.length]);

  return (
    <Box
      sx={{
        width: hideStyle ? "100%" : 280,
        borderRadius: "16px",
        p: hideStyle ? 0 : 2,
        border: hideStyle ? "none" : `1px solid ${borderColor}`,
        backgroundColor: hideStyle
          ? "transparent"
          : (isDark ? "rgba(20,24,33,0.6)" : "rgba(255,255,255,0.82)"),
        backgroundImage: "none",
        backdropFilter: hideStyle ? "none" : "blur(10px)",
        boxShadow: hideStyle ? "none" : (isDark ? "0 4px 16px rgba(0,0,0,0.2)" : "0 4px 14px rgba(15,23,42,0.06)"),
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
        <Box display="flex" gap={1} alignItems="center">
          <SlidersHorizontal size={18} color={textColor} />
          <Typography fontWeight={800} color="text.primary">
            Filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#14b8a6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                color: "white",
                fontWeight: 700,
                ml: 0.5,
              }}
            >
              {activeFiltersCount}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {onClose && (
            <IconButton onClick={onClose} size="small" sx={{ p: 0.5, color: subTextColor }}>
              <X size={18} />
            </IconButton>
          )}
          {activeFiltersCount > 0 && onClearAll && (
            <Typography
              onClick={onClearAll}
              sx={{
                fontSize: 12,
                color: subTextColor,
                cursor: "pointer",
                fontWeight: 500,
                "&:hover": { color: textColor }
              }}
            >
              Clear
            </Typography>
          )}
        </Box>
      </Box>

      {/* Favorites */}
      <Box mb={2}>
        <Box sx={rowSx} onClick={toggleFavoritesOnly}>
          <Checkbox
            checked={filters.favoritesOnly}
            onChange={toggleFavoritesOnly}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={checkboxSx}
          />
          <Typography color="text.primary" fontSize="0.9rem">
            Favorites only
          </Typography>
        </Box>
      </Box>

      {visibleCareCategoryOptions.length > 0 && (
        <>
          <Divider sx={{ my: 2, borderColor }} />

          {/* Categories */}
          <Typography sx={sectionTitleSx}>
            Care Categories
          </Typography>

          <Box
            sx={{
              maxHeight: 300,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: isDark ? 'rgba(148,163,184,0.1)' : 'rgba(0,0,0,0.05)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDark ? 'rgba(148,163,184,0.3)' : 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(148,163,184,0.5)' : 'rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            {visibleCareCategoryOptions.map((opt) => {
              const categoryId = opt.id;
              const categoryName = opt.label;
              const compositions = dynamicCategories[categoryId] || [];
              const expanded = expandedCategories.has(categoryId);

              return (
                <Box key={opt.id}>
                  <Box
                    sx={{ ...rowSx, justifyContent: "space-between" }}
                    onClick={() => toggleCategory(categoryId)}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                    >
                      <Checkbox
                        checked={filters.selectedCategories.includes(categoryId)}
                        onChange={() => toggleCategory(categoryId)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={checkboxSx}
                      />

                      <Typography color="text.primary" fontSize="0.9rem">
                        {categoryName}
                      </Typography>
                    </Box>

                    {compositions.length > 0 && (
                      <IconButton
                        onClick={(e) => { e.stopPropagation(); toggleCategoryExpansion(categoryId); }}
                        size="small"
                        sx={{
                          p: 0.5,
                          color: subTextColor,
                          borderRadius: "6px",
                          '&:hover': { color: "#14b8a6", backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" }
                        }}
                      >
                        {expanded ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </IconButton>
                    )}
                  </Box>

                  {expanded && compositions.length > 0 &&
                    compositions.map((comp) => (
                      <Box
                        key={comp}
                        ml={4.5}
                        sx={rowSx}
                        onClick={() => toggleComposition(comp)}
                      >
                        <Checkbox
                          checked={filters.selectedCompositions.includes(comp)}
                          onChange={() => toggleComposition(comp)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={checkboxSx}
                        />

                        <Typography fontSize={13} color="text.primary">
                          {comp}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              );
            })}
          </Box>

        </>
      )}

      <Divider sx={{ mb: 2, borderColor }} />

      {/* Status */}
      <Typography sx={sectionTitleSx}>
        Status
      </Typography>

      {statusOptions.map((s) => (
        <Box
          key={s.id}
          sx={rowSx}
          onClick={() => toggleStatus(s.id)}
        >
          <Checkbox
            checked={filters.allowedStatuses.includes(
              s.id
            )}
            onChange={() =>
              toggleStatus(s.id)
            }
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={checkboxSx}
          />

          <Typography fontSize={14} color="text.primary">
            {s.label}
          </Typography>
        </Box>
      ))}

      <Divider sx={{ my: 2, borderColor }} />

      {/* Manufacturer */}
      <Typography sx={sectionTitleSx}>
        Manufacturer
      </Typography>

      <Box
        sx={{
          maxHeight: 180,
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: isDark ? "rgba(148,163,184,0.1)" : "rgba(0,0,0,0.05)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "rgba(148,163,184,0.3)" : "rgba(0,0,0,0.2)",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: isDark ? "rgba(148,163,184,0.5)" : "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        {manufacturerOptions.length > 0 ? (
          manufacturerOptions.map((manufacturer) => (
            <Box
              key={manufacturer}
              sx={rowSx}
              onClick={() => toggleManufacturer(manufacturer)}
            >
              <Checkbox
                checked={filters.selectedManufacturers.includes(manufacturer)}
                onChange={() => toggleManufacturer(manufacturer)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={checkboxSx}
              />
              <Typography fontSize={14} color="text.primary">
                {manufacturer}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography fontSize={13} sx={{ color: subTextColor, px: 1, py: 1 }}>
            No manufacturers available
          </Typography>
        )}
      </Box>

      {canAccessPurchaseAndBillFilter && (
        <>
          <Divider sx={{ my: 2, borderColor }} />

          {/* CS-1765 - Purchase & Bill filter for admin and super admin users only */}
          <Typography sx={sectionTitleSx}>
            Purchase And Bill
          </Typography>

          {[
            { label: "Yes", value: "Y" },
            { label: "No", value: "N" },
          ].map((option) => (
            <Box
              key={option.value}
              sx={rowSx}
              onClick={() => togglePurchaseAndBill(option.value)}
            >
              <Checkbox
                checked={filters.selectedPurchaseAndBill.includes(option.value)}
                onChange={() => togglePurchaseAndBill(option.value)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={checkboxSx}
              />
              <Typography fontSize={14} color="text.primary">
                {option.label}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default MarketFilterSidebar;
